import { StackScreenProps } from "@react-navigation/stack";
import { GiftCardPaymentRootParamList } from "../../../../types";
import React from "react";
import {
  Button,
  Divider,
  Page,
  Stack,
  Text,
} from "@towersystems/roam-common-native";
import {
  GiftCard,
  GiftCardTransaction,
} from "@towersystems/roam-common/lib/generated-types";
import {
  QUERY_GIFT_CARD_BY_CODE,
  QUERY_GIFT_CARD_TRANSACTIONS_BY_CODE,
} from "../../../../utilities/gift-card/constants";
import { useLazyQuery } from "@apollo/client";
import { View } from "react-native-ui-lib";
import { useFocusEffect } from "@react-navigation/native";
import { useCheckoutContextProvider } from "../../utils/checkout";
import {
  formatDateToCalendar,
  fullDateTimeFormat,
  moneyToFormattedString,
} from "@towersystems/roam-common/lib/shared-utils";
import { useTranslation } from "react-i18next";

export interface GiftCardPaymentScreenProps
  extends StackScreenProps<
    GiftCardPaymentRootParamList,
    "GiftCardDetailsScreen"
  > {}

export const GiftCardDetailsScreen = ({
  route,
  navigation,
}: GiftCardPaymentScreenProps) => {
  const { t } = useTranslation("gift_card");
  const { balance } = useCheckoutContextProvider();
  const giftCardNumber = route.params.giftCardNumber;
  const [giftCard, setGiftCard] = React.useState<GiftCard | undefined>(
    undefined
  );

  const [giftCardTransactions, setGiftCardTransactions] = React.useState<
    GiftCardTransaction[]
  >([]);

  const [q, { loading }] = useLazyQuery<{
    giftCardByCode: GiftCard;
  }>(QUERY_GIFT_CARD_BY_CODE);

  const [qTransactions, { loading: loadingTransactions }] = useLazyQuery<{
    giftCardTransactionsByCode: GiftCardTransaction[];
  }>(QUERY_GIFT_CARD_TRANSACTIONS_BY_CODE);

  useFocusEffect(
    React.useCallback(() => {
      setGiftCardTransactions([]);
      q({
        variables: {
          code: giftCardNumber,
        },
      })
        .then((res) => {
          if (res.data?.giftCardByCode) {
            setGiftCard(res.data.giftCardByCode);
          }
        })
        .catch((e) => console.log(e));
    }, [])
  );

  React.useEffect(() => {
    if (giftCard) {
      qTransactions({
        variables: {
          giftCode: giftCard.code,
        },
      })
        .then((res) => {
          if (res.data?.giftCardTransactionsByCode) {
            setGiftCardTransactions(res.data.giftCardTransactionsByCode);
          }
        })
        .catch((e) => console.log(e));
    }
  }, [giftCard]);

  const maximumTenderAmount = React.useMemo(() => {
    if (!giftCard) {
      return 0;
    }

    if (giftCard.canBeRedeemed === false || balance <= 0) {
      return 0;
    }

    return Math.min(giftCard.balance, balance);
  }, [balance, giftCard]);

  if (loading) {
    return <Page title={t("title.searching_for_gift_card")} />;
  }

  if (!giftCard) {
    return (
      <Page
        title={t("title.gift_card_not_found")}
        secondaryTitle={t("subtitle.gift_card_not_found", {
          giftCardNumber: giftCardNumber,
        })}
      ></Page>
    );
  }

  const footer = (
    <Stack fill>
      <View flex>
        <Button
          fullWidth
          onPress={() => {
            navigation.goBack();
          }}
          label={t("button.pay_with_different_gift_card")}
          isDestructive
        ></Button>
      </View>
      <View flex>
        <Button
          fullWidth
          disabled={maximumTenderAmount <= 0}
          onPress={() => {
            navigation.navigate("GiftCardPaymentScreen", {
              giftCardNumber: giftCardNumber,
              giftCardId: giftCard.id,
              maximumTenderAmount: maximumTenderAmount,
              giftCardBalance: giftCard.balance,
            });
          }}
          isAction
          label={t("button.pay_with_gift_card")}
        ></Button>
      </View>
    </Stack>
  );

  const transactionMarkup =
    loadingTransactions == false &&
    giftCardTransactions.map((transaction) => {
      return (
        <View key={transaction.id}>
          <Stack fill>
            <View
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <View>
                <Text variant="heading1">
                  {formatDateToCalendar(
                    transaction.transactionDate,
                    fullDateTimeFormat
                  )}
                </Text>
              </View>
              <View>
                <View>
                  <Text style={{ textAlign: "right" }} color="subdued">
                    {transaction.transactionType}
                  </Text>
                  <Text style={{ textAlign: "right" }} variant="heading1">
                    {moneyToFormattedString(transaction.amount)}
                  </Text>
                </View>
              </View>
            </View>
          </Stack>
          <Divider />
        </View>
      );
    });

  return (
    <Page
      title={t("title.gift_card_found")}
      secondaryTitle={
        <Text
          style={{ textAlign: "center" }}
        >{`Gift card number: ${giftCardNumber}`}</Text>
      }
      titleContainerStyle={{ alignItems: "center", justifyContent: "center" }}
      titleTextStyle={{ textAlign: "center" }}
      footer={footer}
    >
      <Stack vertical>
        <View>
          <Stack>
            <View
              style={{
                alignItems: "center",
                flex: 1,
              }}
            >
              <Text color="subdued">Card Balance</Text>
              <Text variant="heading">
                {moneyToFormattedString(giftCard.balance)}
              </Text>
            </View>
            <View
              style={{
                alignItems: "center",
                flex: 1,
              }}
            >
              <Text color="subdued">Balance Redeemed</Text>
              <Text variant="heading">{moneyToFormattedString(0)}</Text>
            </View>
            <View
              style={{
                alignItems: "center",
                flex: 1,
              }}
            >
              <Text color="subdued">Expiry Date</Text>
              <Text variant="heading">
                {formatDateToCalendar(giftCard.expiryDate)}
              </Text>
            </View>
          </Stack>
        </View>
        <Divider />
        {transactionMarkup}
      </Stack>
    </Page>
  );
};
